import { GameType } from '@daymade/common-lib/dbEnums';
import { oneOfOrUndefined } from '@daymade/common-lib/types';
import { DataProvider, GlobalActionsProvider } from '@plasmicapp/host';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo } from 'react';
import { SubscriberStatus } from 'utils/constants';
import { resizeImageUrl } from 'utils/image/imageResizeService';

export const UserGlobalContext = observer(
  ({ children }: React.PropsWithChildren) => {
    const router = useRouter();
    const {
      wishListPrizes,
      eligibleForIntroOffer,
      eligibleForUpgrade,
      currentUser,
      balances,
      activeWeeklyDrawSubscriptionTickets,
      initialized,
      impactShareOptions,
      activeSubscriptions,
      subscriptionsCount,
      upcomingSubscriptionDrawDate,
      leaderboardStats,
      setAvatarIcon,
      fetchWishListPrizes,
      toggleWishlistPrize,
      submitHelpQuestion,
      submitAccountDetails,
      logout,
      subscriptionStatusInfo,
    } = useUserStore();

    const { isPremiumSubscriber } = subscriptionStatusInfo;

    const { getAccountLink } = useUIStore();

    const subscription = useMemo(() => {
      if (activeSubscriptions.length > 0) {
        return {
          ...activeSubscriptions[0].subscription,
          state: activeSubscriptions[0].subscription.status,
        };
      }
      return null; // Return null or an empty object if no subscriptions are available
    }, [activeSubscriptions]);

    const subscriberDrawTypePlanCode = useMemo(() => {
      // This assumes that the user has at most one active subscription, however, this is not always the case if a user's
      // data has gone wonky. The UI cannot handle this situation, so only check their first subscription (as we did before)
      const drawTypePlanCodes = activeSubscriptions[0]?.drawSchedulePlans.map(x => x.drawTypePlan.code);
      if (drawTypePlanCodes?.length) {
        const areAllSame = drawTypePlanCodes.every(x => x === drawTypePlanCodes[0]);
        if (!areAllSame) {
          // The user has mixed Draw Type Plan Codes on their first active subscription. This means they
          // have an active subscription, so we should not default to no subscription. Instead, use the first subscription
          console.warn(`User ${currentUser?.id} had mixed drawTypePlanCodes ${drawTypePlanCodes.join(',')}`);
        }

        const drawTypePlanCode = oneOfOrUndefined(drawTypePlanCodes[0], ...Object.values(SubscriberStatus));
        if (drawTypePlanCode == undefined) {
          console.warn(`User ${currentUser?.id} had a drawTypePlanCode ${drawTypePlanCodes[0]} that did not match SubscriberStatus values`);
        } else if (drawTypePlanCode !== 'NON_SUBSCRIBER') {
          // The user has a valid draw type plan code that is not 'NON_SUBSCRIBER', return that value
          return drawTypePlanCode;
        }
      }
      return null; // Default status if no tickets are available, or if we have a weird subscription type
    }, [currentUser, activeSubscriptions]);


    return (
      <GlobalActionsProvider
        contextName="UserGlobalContext"
        actions={{
          setLeaderboardPicture: setAvatarIcon,
          fetchWishListPrizes,
          toggleWishlistPrize,
          submitHelpQuestion,
          submitAccountDetails,
          logout,
        }}
      >
        <DataProvider
          name="user"
          data={{
            initialized: initialized,
            currentUser: currentUser
              ? {
                  ...currentUser,
                  email_address: currentUser?.emailAddress,
                  // The property key 'subscriberStatus' is incorrectly named, it is really the subscriberDrawTypePlanCode.
                  // However, it is referenced all over plasmic and hard to rename
                  subscriberStatus: subscriberDrawTypePlanCode,
                  trees_planted: currentUser?.treesPlanted ?? 0,
                  tokenBalance: balances?.tokenBalance ?? 0,
                  leaderboard_username: currentUser?.friendlyName ?? '',
                  leaderboard_photo_location: currentUser?.avatarLocation
                    ? resizeImageUrl(currentUser.avatarLocation, { width: 300, height: 300})
                    : '',
                  leaderboardStats: {
                    points: leaderboardStats?.pointsTotal ?? 0,
                  },
                }
              : null,
            impactShareOptions,
            subscription,
            upcomingDrawTickets: [
              {
                entries: activeWeeklyDrawSubscriptionTickets[0]?.entries.map(
                  (entry) => {
                    const numbers =
                      entry.type === GameType.LOTTERY
                        ? `${entry.main.join(',')}|${entry.bonus.join(',')}`
                        : '';
                    return {
                      numbers,
                      ticket_type:
                        activeWeeklyDrawSubscriptionTickets[0].drawTypePlan
                          .drawTypeCode === 'PREMIUM'
                          ? 'SUBSCRIPTION'
                          : 'LITE_SUBSCRIPTION',
                    };
                  }
                ),
              },
            ],
            eligibleForIntroOffer,
            eligibleForUpgrade,
            // The property key 'subscriberStatus' is incorrectly named, it is really the subscriberDrawTypePlanCode.
            // However, it is referenced all over plasmic and hard to rename
            subscriberStatus: subscriberDrawTypePlanCode,
            subscriptionsCount,
            upcomingSubscriptionDrawDate,
            isPremiumSubscriber,
            wishListPrizes,
          }}
        >
          {children}
        </DataProvider>
      </GlobalActionsProvider>
    );
  }
);
